import { motion, useReducedMotion } from "framer-motion";
import { ShieldAlert } from "lucide-react";

export type RiskLevel = "Low" | "Medium" | "High" | "Severe";

interface Props {
  level: RiskLevel;
  label?: string;
  /** Short hint shown beneath the bar. */
  hint?: string;
}

const LEVELS: Record<RiskLevel, { color: string; bars: number }> = {
  Low: { color: "152 70% 55%", bars: 1 },
  Medium: { color: "38 95% 60%", bars: 2 },
  High: { color: "12 92% 60%", bars: 3 },
  Severe: { color: "0 78% 58%", bars: 4 },
};

const RiskMeter = ({ level, label = "Risk exposure", hint }: Props) => {
  const reduce = useReducedMotion();
  const cfg = LEVELS[level];
  return (
    <div
      className="rounded-xl border p-3 backdrop-blur-md"
      style={{
        borderColor: `hsl(${cfg.color} / 0.4)`,
        background: `hsl(${cfg.color} / 0.08)`,
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-white/65">
          <ShieldAlert className="h-3 w-3" />
          {label}
        </div>
        <span
          className="font-mono text-xs font-semibold uppercase tracking-wider"
          style={{ color: `hsl(${cfg.color})` }}
        >
          {level}
        </span>
      </div>
      <div className="mt-2 flex gap-1">
        {[1, 2, 3, 4].map((i) => (
          <motion.div
            key={i}
            initial={{ scaleX: reduce ? 1 : 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: reduce ? 0 : 0.35, delay: reduce ? 0 : i * 0.06 }}
            className="h-1.5 flex-1 origin-left rounded-full"
            style={{
              background:
                i <= cfg.bars
                  ? `hsl(${cfg.color})`
                  : "hsl(0 0% 100% / 0.08)",
              boxShadow:
                i <= cfg.bars ? `0 0 8px hsl(${cfg.color} / 0.6)` : "none",
            }}
          />
        ))}
      </div>
      {hint && <p className="mt-2 text-[11px] text-white/60">{hint}</p>}
    </div>
  );
};

export default RiskMeter;